import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMetaStore } from '../meta/metaStore'
import {
  RESEARCH_BASE_MAX_RANK,
  RESEARCH_BRANCH_IDS,
  RESEARCH_MAX_RANK,
  RESEARCH_TOWER_IDS,
  type ResearchBranchId,
  type ResearchTowerId
} from '../game/combat/research/types'
import {
  powerEffectLabel,
  rangeEffectLabel,
  tempoEffectLabel
} from '../game/combat/research/modifiers'
import { isAdvancedRank, researchCostForNextRank } from '../game/combat/research/costs'
import { TOWER_CATALOG } from '../game/model/towers'
import { getAudioManager } from '../game/audio'

const TOWER_ROLE: Record<ResearchTowerId, string> = {
  fire: 'General DPS',
  frost: 'Control / slow',
  cannon: 'Ground splash',
  poison: 'DoT specialist',
  aa: 'Air specialist',
  mage: 'Armor Break'
}

const BRANCH_NAME: Record<ResearchBranchId, string> = {
  power: 'Power',
  range: 'Range',
  tempo: 'Tempo'
}

/** CSS hex + readable glyph color for dark tower chips (e.g. Cannon). */
function glyphStyle(color: number | undefined): React.CSSProperties {
  const c = color ?? 0xffffff
  const r = (c >> 16) & 0xff
  const g = (c >> 8) & 0xff
  const b = c & 0xff
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return {
    backgroundColor: `#${c.toString(16).padStart(6, '0')}`,
    color: luminance < 0.45 ? '#f8fafc' : '#0f172a'
  }
}

function branchEffectLabel(
  towerId: ResearchTowerId,
  branch: ResearchBranchId,
  rank: number
): string {
  if (branch === 'power') return powerEffectLabel(towerId, rank)
  if (branch === 'range') return rangeEffectLabel(rank)
  return tempoEffectLabel(rank)
}

function ProgressPips({
  filled,
  total,
  kind
}: {
  filled: number
  total: number
  kind: 'base' | 'advanced'
}) {
  return (
    <span
      className={`research-lab__pips research-lab__pips--${kind}`}
      aria-hidden
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`research-lab__pip${i < filled ? ' research-lab__pip--on' : ''}`}
        />
      ))}
    </span>
  )
}

/**
 * Compact Research lab — tower grid + branch cards (M63).
 * Match Levels (Coins) remain a separate axis.
 */
export const ResearchLab: React.FC = () => {
  const ingots = useMetaStore((s) => s.ingots)
  const research = useMetaStore((s) => s.research)
  const purchase = useMetaStore((s) => s.purchase)
  const canAfford = useMetaStore((s) => s.canAfford)
  const isAdvancedLocked = useMetaStore((s) => s.isAdvancedLocked)
  const advancedUnlocked = useMetaStore((s) => s.advancedResearchUnlocked)
  const [selectedId, setSelectedId] = useState<ResearchTowerId>('fire')
  const [flashKey, setFlashKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const selected = research.towers[selectedId]
  const def = TOWER_CATALOG[selectedId]

  const onPurchase = useCallback(
    (branch: ResearchBranchId) => {
      if (busy) return
      setBusy(true)
      try {
        const result = purchase(selectedId, branch)
        if (result.ok) {
          getAudioManager().play('ui.confirm')
          setFlashKey(`${selectedId}-${branch}-${result.newRank}`)
          window.setTimeout(() => setFlashKey(null), 420)
        }
      } finally {
        setBusy(false)
      }
    },
    [busy, purchase, selectedId]
  )

  return (
    <section className="research-lab research-lab--v2" aria-label="Tower Research">
      <header className="research-lab__header">
        <div className="research-lab__title-row">
          <h1 className="research-lab__title">Research</h1>
          <Link to="/" className="research-lab__back" data-testid="research-back-home">
            ← Home
          </Link>
        </div>
        <div className="research-lab__meta-row">
          <p
            className={`research-lab__advanced${
              advancedUnlocked ? ' research-lab__advanced--open' : ' research-lab__advanced--locked'
            }`}
            data-testid="research-advanced-status"
          >
            {advancedUnlocked ? 'Advanced unlocked' : 'Advanced locked · finish Stage 1 Normal+'}
          </p>
        </div>
      </header>

      <ul className="research-lab__tower-grid" data-testid="research-tower-list" role="list">
        {RESEARCH_TOWER_IDS.map((id) => {
          const ranks = research.towers[id]
          const tower = TOWER_CATALOG[id]
          const active = id === selectedId
          const baseDone =
            ranks.power >= RESEARCH_BASE_MAX_RANK.power &&
            ranks.range >= RESEARCH_BASE_MAX_RANK.range &&
            ranks.tempo >= RESEARCH_BASE_MAX_RANK.tempo
          return (
            <li key={id}>
              <button
                type="button"
                className={`research-lab__tower-chip${active ? ' research-lab__tower-chip--active' : ''}`}
                data-testid={`research-tower-${id}`}
                aria-pressed={active}
                aria-current={active ? 'true' : undefined}
                aria-label={`${tower?.name ?? id}, ${TOWER_ROLE[id]}${active ? ', selected' : ''}`}
                onClick={() => setSelectedId(id)}
              >
                <span
                  className="research-lab__glyph"
                  style={glyphStyle(tower?.color)}
                  aria-hidden
                >
                  {tower?.letter ?? '?'}
                </span>
                <span className="research-lab__tower-chip-name">{tower?.name ?? id}</span>
                {active ? (
                  <span className="research-lab__tower-chip-selected" aria-hidden>
                    ●
                  </span>
                ) : null}
                {baseDone ? (
                  <span className="research-lab__tower-chip-badge" title="Base complete">
                    ★
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>

      <div className="research-lab__detail" data-testid="research-detail">
        <div className="research-lab__selected-head">
          <span
            className="research-lab__glyph research-lab__glyph--lg"
            style={glyphStyle(def?.color)}
            aria-hidden
          >
            {def?.letter ?? '?'}
          </span>
          <div>
            <h2 className="research-lab__detail-title">{def?.name ?? selectedId}</h2>
            <p className="research-lab__detail-role">{TOWER_ROLE[selectedId]}</p>
          </div>
        </div>

        <ul className="research-lab__branches">
          {RESEARCH_BRANCH_IDS.map((branch) => {
            const rank = selected[branch]
            const max = RESEARCH_MAX_RANK[branch]
            const baseMax = RESEARCH_BASE_MAX_RANK[branch]
            const advancedMax = max - baseMax
            const baseFilled = Math.min(rank, baseMax)
            const advancedFilled = Math.max(0, rank - baseMax)
            const atMax = rank >= max
            const cost = researchCostForNextRank(branch, rank)
            const advancedLocked = isAdvancedLocked(selectedId, branch)
            const affordable = canAfford(selectedId, branch)
            const currentLabel = branchEffectLabel(selectedId, branch, rank)
            const nextLabel = atMax
              ? 'Max'
              : branchEffectLabel(selectedId, branch, rank + 1)
            const flash = flashKey === `${selectedId}-${branch}-${rank}`
            const inAdvanced = rank >= baseMax
            const disabled = atMax || advancedLocked || !affordable || busy

            let buyLabel = cost == null ? 'MAX' : `RESEARCH · ${cost} ◆`
            if (atMax) buyLabel = 'MAX'
            else if (advancedLocked) buyLabel = 'ADVANCED LOCKED'
            else if (!affordable && cost != null) {
              const need = Math.max(1, cost - ingots)
              buyLabel = `Need ${need}◆`
            }

            return (
              <li
                key={branch}
                className={`research-lab__branch${flash ? ' research-lab__branch--flash' : ''}${
                  inAdvanced ? ' research-lab__branch--advanced' : ''
                }`}
                data-testid={`research-branch-${branch}`}
              >
                <div className="research-lab__branch-head">
                  <h3 className="research-lab__branch-name">{BRANCH_NAME[branch]}</h3>
                  <p
                    className="research-lab__branch-rank"
                    aria-label={`${BRANCH_NAME[branch]} Base ${baseFilled} of ${baseMax}${
                      advancedUnlocked
                        ? `, Advanced ${advancedFilled} of ${advancedMax}`
                        : ''
                    }`}
                    data-testid={`research-status-${branch}`}
                  >
                    Base {baseFilled}/{baseMax}
                    {advancedUnlocked ? (
                      <>
                        {' '}
                        · Adv {advancedFilled}/{advancedMax}
                      </>
                    ) : null}
                  </p>
                </div>

                <div className="research-lab__progress" aria-hidden>
                  <ProgressPips filled={baseFilled} total={baseMax} kind="base" />
                  {advancedUnlocked || advancedFilled > 0 ? (
                    <>
                      <span className="research-lab__pip-sep" title="Base · Advanced" />
                      <ProgressPips filled={advancedFilled} total={advancedMax} kind="advanced" />
                    </>
                  ) : (
                    <span className="research-lab__adv-lock-hint">Adv locked</span>
                  )}
                </div>

                <p className="research-lab__branch-effect">
                  <span className="research-lab__eff-now">{currentLabel}</span>
                  {!atMax ? (
                    <>
                      <span className="research-lab__eff-arrow" aria-hidden>
                        →
                      </span>
                      <span className="research-lab__eff-next">{nextLabel}</span>
                    </>
                  ) : null}
                </p>

                <button
                  type="button"
                  className={`research-lab__buy${
                    !affordable && !atMax && !advancedLocked ? ' research-lab__buy--need' : ''
                  }`}
                  data-testid={`research-buy-${branch}`}
                  disabled={disabled}
                  aria-label={
                    atMax
                      ? `${BRANCH_NAME[branch]} max rank`
                      : advancedLocked
                        ? `${BRANCH_NAME[branch]} Advanced locked`
                        : !affordable && cost != null
                          ? `${BRANCH_NAME[branch]} needs ${Math.max(1, cost - ingots)} more Ingots`
                          : `Research ${BRANCH_NAME[branch]} for ${cost ?? 0} Ingots`
                  }
                  onClick={() => onPurchase(branch)}
                >
                  {buyLabel}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}
